import 'swiper/css'
import 'swiper/css/effect-fade'

import { Children, useCallback, useEffect, useState } from 'react'

import { ArrowLeftIcon, ArrowRightIcon, Button } from '@cash-me/react-components'
import { EffectFade, Navigation } from 'swiper/modules' // import swiper required modules
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'

import { CarouselProps } from './carousel.model'
import * as S from './styles'

export const Carousel = ({
  carouselStyle = {},
  navigation = false,
  onChangeIndex = null,
  children,
  swiperOptions,
  setOutsideSwiperRef,
  ...props
}: CarouselProps) => {
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const handleChangeIndex = useCallback(() => {
    if (!swiperRef) return

    setCurrentIndex(swiperRef.realIndex)
    if (onChangeIndex) onChangeIndex(swiperRef.realIndex)
  }, [onChangeIndex, swiperRef])

  const slideNext = useCallback(() => {
    if (!swiperRef) return

    swiperRef.slideNext()
  }, [swiperRef])

  const slidePrev = useCallback(() => {
    if (!swiperRef) return

    swiperRef.slidePrev()
  }, [swiperRef])

  const slideTo = useCallback(
    (index: number) => {
      if (!swiperRef) return

      swiperRef.slideTo(index)
    },
    [swiperRef]
  )

  const renderControl = () => (
    <S.ControlsWrapper className="controls-wrapper">
      <S.SlideControls>
        {Children.map(children, (_, index) => (
          <S.SlideControlItem
            data-state={currentIndex === index ? 'active' : 'inactive'}
            onClick={() => slideTo(index)}
          />
        ))}
      </S.SlideControls>
      <S.ArrowControls>
        <Button aria-label="Slide anterior" variants="icon-only" size="sm" onClick={slidePrev}><ArrowLeftIcon /></Button>
        <Button aria-label="Próximo slide" variants="icon-only" size="sm" onClick={slideNext}><ArrowRightIcon /></Button>
      </S.ArrowControls>
    </S.ControlsWrapper>
  )

  const renderItems = useCallback(() => {
    if (!Children) return null
    return Children.map(children, child => <SwiperSlide>{child}</SwiperSlide>)
  }, [children])

  useEffect(() => {
    if (swiperRef && setOutsideSwiperRef) setOutsideSwiperRef(swiperRef)
  }, [swiperRef, setOutsideSwiperRef])

  return (
    <>
      <S.CarouselWrapper>
        <Swiper
          onSwiper={setSwiperRef}
          onSlideChange={handleChangeIndex}
          spaceBetween={16}
          slidesPerView={1}
          keyboard
          style={carouselStyle}
          modules={[EffectFade, Navigation]}
          resistance={true}
          resistanceRatio={0}
          {...swiperOptions}
          {...props}
        >
          {renderItems()}
        </Swiper>
      </S.CarouselWrapper>
      {swiperRef && navigation && renderControl()}
    </>
  )
}
